/**
 * Error propagation engine for stabilizer circuits.
 *
 * Tracks Pauli errors through a circuit in the Heisenberg picture:
 * each qubit carries a 2-bit label (x, z) encoding I=(0,0), X=(1,0), Z=(0,1), Y=(1,1).
 *
 * Conjugation rules through Clifford gates:
 *   H:  X↔Z,  Y→Y
 *   CX(c,t): X_c→X_c X_t,  Z_t→Z_c Z_t,  X_t→X_t,  Z_c→Z_c
 *   CZ(a,b): X_a→X_a Z_b,  X_b→Z_a X_b,  Z_a→Z_a,  Z_b→Z_b
 *   S:  X→Y,  Z→Z,  Y→-X (same up to phase: X→Y, Y→X for tracking)
 *   M:  X or Y error flips outcome, Z does not.
 */

import { MeasurementGate } from '../types/stabilizer';

// Per-qubit Pauli error state: 2-bit (hasX, hasZ)
// I=(false,false), X=(true,false), Z=(false,true), Y=(true,true)
export interface QubitPauli {
  x: boolean;
  z: boolean;
}

export function pauliLabel(p: QubitPauli): string {
  if (p.x && p.z) return 'Y';
  if (p.x) return 'X';
  if (p.z) return 'Z';
  return 'I';
}

export function isNonTrivial(p: QubitPauli): boolean {
  return p.x || p.z;
}

/** An error injected at a specific point in the circuit */
export interface InjectedError {
  id: string;
  /** Global qubit index */
  qubitIndex: number;
  /** Column index in the flat gate list (error occurs BEFORE this gate) */
  beforeGateCol: number;
  /** Pauli type */
  pauli: 'X' | 'Y' | 'Z';
}

/** A snapshot of the error state at a given point */
export interface PropagationSnapshot {
  /** Column index in flat gate list */
  col: number;
  /** Pauli on each qubit (indexed by global qubit index) */
  state: Map<number, QubitPauli>;
}

/** Result of propagation: which measurements are flipped */
export interface PropagationResult {
  /** Full trace: error state after each gate */
  snapshots: PropagationSnapshot[];
  /** Which measurement indices (0-based in flat list) are flipped */
  flippedMeasurements: number[];
  /** Which detector indices are flipped (parity of their measurements) */
  flippedDetectors: number[];
  /** Which observable indices are flipped */
  flippedObservables: number[];
  /** Final Pauli state on all qubits after the full circuit */
  finalState: Map<number, QubitPauli>;
}

/** The flat compiled circuit: a list of gates with global qubit indices */
export interface FlatGateEntry {
  gate: string;          // H, CX, CZ, M, R
  targets: number[];     // global qubit indices
  col: number;           // column index in flat list
  stabIndex: number;     // which stabilizer this belongs to
  measurementIndex?: number; // for M gates, 0-based index in measurement list
}

/**
 * Build the flat gate list from resolved circuits and schedule,
 * assigning measurement indices.
 */
export function buildFlatCircuit(
  resolvedCircuits: { stabIndex: number; circuit: MeasurementGate[] }[],
  schedule: { stabIndex: number }[],
): FlatGateEntry[] {
  const flat: FlatGateEntry[] = [];
  let col = 0;
  let measIdx = 0;

  for (const entry of schedule) {
    const rc = resolvedCircuits[entry.stabIndex];
    if (!rc) continue;

    for (const g of rc.circuit) {
      const fe: FlatGateEntry = {
        gate: g.gate,
        targets: g.targets,
        col,
        stabIndex: entry.stabIndex,
      };
      if (g.gate === 'M') {
        fe.measurementIndex = measIdx++;
      }
      flat.push(fe);
      col++;
    }
    col++; // gap between stabilizers
  }

  return flat;
}

/**
 * Propagate injected errors through the flat circuit.
 */
export function propagateErrors(
  flatCircuit: FlatGateEntry[],
  injectedErrors: InjectedError[],
  detectorMeasIndices: number[][],
  observableMeasIndices: number[][],
): PropagationResult {
  // Initialize error state from injections
  const state = new Map<number, QubitPauli>();

  const getP = (qi: number): QubitPauli => state.get(qi) ?? { x: false, z: false };
  const setP = (qi: number, p: QubitPauli) => {
    if (p.x || p.z) state.set(qi, p);
    else state.delete(qi);
  };

  // XOR a Pauli onto current state
  const applyPauli = (qi: number, pauli: 'X' | 'Y' | 'Z') => {
    const cur = getP(qi);
    if (pauli === 'X') setP(qi, { x: !cur.x, z: cur.z });
    else if (pauli === 'Z') setP(qi, { x: cur.x, z: !cur.z });
    else setP(qi, { x: !cur.x, z: !cur.z }); // Y
  };

  const snapshots: PropagationSnapshot[] = [];
  const flippedMeasurements: number[] = [];

  // Sort gates by col
  const sorted = [...flatCircuit].sort((a, b) => a.col - b.col);

  // Group injected errors by the column they occur before
  const errorsByCol = new Map<number, InjectedError[]>();
  for (const err of injectedErrors) {
    const list = errorsByCol.get(err.beforeGateCol) ?? [];
    list.push(err);
    errorsByCol.set(err.beforeGateCol, list);
  }

  // Also inject errors that are past the last gate
  const maxCol = sorted.length > 0 ? sorted[sorted.length - 1].col + 1 : 0;

  for (const gate of sorted) {
    // Inject errors before this gate
    const errs = errorsByCol.get(gate.col);
    if (errs) {
      for (const err of errs) {
        applyPauli(err.qubitIndex, err.pauli);
      }
    }

    // Take snapshot before gate application (showing error state going into gate)
    snapshots.push({
      col: gate.col,
      state: new Map(state),
    });

    // Apply gate conjugation to error
    const t = gate.targets;
    switch (gate.gate) {
      case 'H': {
        // H: X↔Z, Y→Y
        const p = getP(t[0]);
        setP(t[0], { x: p.z, z: p.x });
        break;
      }
      case 'CX': {
        // CX(control=t[0], target=t[1])
        // X on control → XX (X propagates to target)
        // Z on target → ZZ (Z propagates to control)
        const pc = getP(t[0]);
        const pt = getP(t[1]);
        // New target X = pc.x XOR pt.x (X from control propagates)
        // New control Z = pc.z XOR pt.z (Z from target propagates)
        setP(t[0], { x: pc.x, z: pc.z !== pt.z });
        setP(t[1], { x: pc.x !== pt.x, z: pt.z });
        break;
      }
      case 'CZ': {
        // CZ(a=t[0], b=t[1])
        // X on a → XZ (X on a generates Z on b)
        // X on b → ZX (X on b generates Z on a)
        const pa = getP(t[0]);
        const pb = getP(t[1]);
        setP(t[0], { x: pa.x, z: pa.z !== pb.x });
        setP(t[1], { x: pb.x, z: pb.z !== pa.x });
        break;
      }
      case 'M': {
        // Measurement: X or Y error flips outcome
        const p = getP(t[0]);
        if (p.x && gate.measurementIndex !== undefined) {
          flippedMeasurements.push(gate.measurementIndex);
        }
        // After measurement, the qubit collapses — error on this qubit resets
        // (In a stabilizer circuit the ancilla is typically reset afterward)
        // We keep the Z component as it doesn't affect the measurement
        // but clear X since it was "consumed" by the measurement
        // Actually in Stim model: after M, the qubit retains its error
        // The measurement records the flip. We don't reset here.
        break;
      }
      case 'R': {
        // Reset: clears all errors on this qubit
        state.delete(t[0]);
        break;
      }
    }
  }

  // Inject errors past last gate
  for (const [col, errs] of errorsByCol) {
    if (col >= maxCol) {
      for (const err of errs) {
        applyPauli(err.qubitIndex, err.pauli);
      }
    }
  }

  // Final snapshot
  snapshots.push({ col: maxCol, state: new Map(state) });

  // Compute flipped detectors
  const flippedDetectors: number[] = [];
  for (let di = 0; di < detectorMeasIndices.length; di++) {
    const indices = detectorMeasIndices[di];
    let parity = 0;
    for (const mi of indices) {
      if (flippedMeasurements.includes(mi)) parity ^= 1;
    }
    if (parity === 1) flippedDetectors.push(di);
  }

  // Compute flipped observables
  const flippedObservables: number[] = [];
  for (let oi = 0; oi < observableMeasIndices.length; oi++) {
    const indices = observableMeasIndices[oi];
    let parity = 0;
    for (const mi of indices) {
      if (flippedMeasurements.includes(mi)) parity ^= 1;
    }
    if (parity === 1) flippedObservables.push(oi);
  }

  return {
    snapshots,
    flippedMeasurements,
    flippedDetectors,
    flippedObservables,
    finalState: new Map(state),
  };
}

// ── QEPG (Quantum Error Propagation Graph) ──

export interface QEPGNode {
  id: string;
  label: string;
  type: 'data' | 'syndrome' | 'detector' | 'observable';
  /** For gate nodes: global qubit index */
  qubitIndex?: number;
  /** For gate nodes: column in flat circuit */
  col?: number;
}

export interface QEPGEdge {
  from: string;
  to: string;
  /** Pauli type of the propagation */
  pauliType: 'X' | 'Z' | 'Y';
}

export interface QEPG {
  nodes: QEPGNode[];
  edges: QEPGEdge[];
}

/**
 * Build the QEPG by injecting single-qubit errors at each data qubit
 * (before any gate) and tracing which detectors/observables flip.
 */
export function buildQEPG(
  flatCircuit: FlatGateEntry[],
  nDataQubits: number,
  detectorMeasIndices: number[][],
  observableMeasIndices: number[][],
  detectorNames: string[],
  observableNames: string[],
): QEPG {
  const nodes: QEPGNode[] = [];
  const edges: QEPGEdge[] = [];

  // Create data qubit nodes
  for (let qi = 0; qi < nDataQubits; qi++) {
    nodes.push({ id: `d${qi}`, label: `q${qi}`, type: 'data', qubitIndex: qi });
  }

  // Create detector nodes
  for (let di = 0; di < detectorMeasIndices.length; di++) {
    nodes.push({ id: `D${di}`, label: detectorNames[di] ?? `D${di}`, type: 'detector' });
  }

  // Create observable nodes
  for (let oi = 0; oi < observableMeasIndices.length; oi++) {
    nodes.push({ id: `O${oi}`, label: observableNames[oi] ?? `O${oi}`, type: 'observable' });
  }

  // For each data qubit, inject X, Z, Y errors and trace
  const firstCol = flatCircuit.length > 0 ? flatCircuit[0].col : 0;

  for (let qi = 0; qi < nDataQubits; qi++) {
    for (const pauli of ['X', 'Z', 'Y'] as const) {
      const err: InjectedError = {
        id: `qepg_${qi}_${pauli}`,
        qubitIndex: qi,
        beforeGateCol: firstCol,
        pauli,
      };

      const result = propagateErrors(flatCircuit, [err], detectorMeasIndices, observableMeasIndices);

      // Add edges to flipped detectors
      for (const di of result.flippedDetectors) {
        edges.push({ from: `d${qi}`, to: `D${di}`, pauliType: pauli });
      }

      // Add edges to flipped observables
      for (const oi of result.flippedObservables) {
        edges.push({ from: `d${qi}`, to: `O${oi}`, pauliType: pauli });
      }
    }
  }

  return { nodes, edges };
}
